import { useCallback, useEffect, useState } from 'react';
import { PageHeader } from '@/components/layout/PageHeader';
import { marketingApi, type MarketingCampaign } from '@/api/marketing.api';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { KeyValueTable } from '@/components/ui/KeyValueTable';
import { showApiError } from '@/utils/apiError';
import { ATTRIBUTION_MODELS } from './constants';

function downloadBlob(data: string, filename: string) {
  const blob = new Blob([data], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function MarketingReportsPage() {
  const [campaigns, setCampaigns] = useState<MarketingCampaign[]>([]);
  const [campaignId, setCampaignId] = useState<number | ''>('');
  const [model, setModel] = useState('');
  const [attribution, setAttribution] = useState<Record<string, unknown> | null>(null);
  const [loadingAttr, setLoadingAttr] = useState(false);

  const loadCampaigns = useCallback(async () => {
    try {
      setCampaigns(await marketingApi.listCampaigns());
    } catch (err) {
      showApiError(err, 'Failed to load campaigns');
    }
  }, []);

  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  const exportCsv = async (kind: 'campaigns' | 'journeys') => {
    try {
      const res = kind === 'campaigns'
        ? await marketingApi.exportCampaignsCsv()
        : await marketingApi.exportJourneysCsv();
      downloadBlob(res.data as string, `marketing-${kind}.csv`);
    } catch (err) {
      showApiError(err, 'Export failed');
    }
  };

  const loadAttribution = async () => {
    if (campaignId === '') return;
    setLoadingAttr(true);
    try {
      setAttribution(await marketingApi.getAttribution(Number(campaignId), model || undefined));
    } catch (err) {
      showApiError(err, 'Failed to load attribution');
    } finally {
      setLoadingAttr(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Reports"
        description="Export campaign and journey data, and inspect multi-touch attribution per campaign."
        breadcrumbs={[
          { label: 'Org', to: '/org' },
          { label: 'Marketing', to: '/marketing' },
          { label: 'Reports' },
        ]}
      />

      <section className="card p-6 space-y-4">
        <h2 className="text-sm font-semibold text-slate-800">Exports</h2>
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" onClick={() => void exportCsv('campaigns')}>
            Campaigns CSV
          </Button>
          <Button variant="secondary" onClick={() => void exportCsv('journeys')}>
            Journeys CSV
          </Button>
        </div>
      </section>

      <section className="card p-6 space-y-4">
        <h2 className="text-sm font-semibold text-slate-800">Attribution</h2>
        <div className="flex flex-wrap gap-3 items-end">
          <div className="min-w-[200px]">
            <Select
              label="Campaign"
              placeholder="Select campaign"
              options={campaigns.map((c) => ({ value: String(c.id), label: c.campaignName }))}
              value={campaignId === '' ? '' : String(campaignId)}
              onChange={(v) => setCampaignId(v ? Number(v) : '')}
            />
          </div>
          <div className="min-w-[160px]">
            <Select
              label="Model"
              placeholder="All models"
              options={ATTRIBUTION_MODELS.map((m) => ({ value: m.value, label: m.label }))}
              value={model}
              onChange={setModel}
            />
          </div>
          <Button loading={loadingAttr} onClick={() => void loadAttribution()} disabled={campaignId === ''}>
            Load report
          </Button>
        </div>
        {attribution && <KeyValueTable data={attribution} embedded emptyTitle="No attribution data" />}
      </section>
    </div>
  );
}

